import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import {
  BookingPageImageKind,
  removeBookingPageImage,
  uploadBookingPageImage,
} from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import HeroPresetPicker from "./HeroPresetPicker";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024;

/**
 * Landing-page images: the hero background and the profile photo. Files are
 * uploaded immediately on selection; replacing an image swaps it in place.
 */
const WebsiteImagesCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  if (!settings?.enabled) {
    return null;
  }

  const handleFile = (kind: BookingPageImageKind, file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_BYTES) {
      // The API re-validates; this just spares a doomed upload.
      alert(t("website.imageInvalid", "Please choose a JPEG, PNG or WebP image up to 8 MB."));
      return;
    }
    dispatch(uploadBookingPageImage({ kind, file }));
  };

  const slots: Array<{
    kind: BookingPageImageKind;
    label: string;
    hint: string;
    url: string | null | undefined;
    inputRef: React.RefObject<HTMLInputElement | null>;
    previewClass: string;
  }> = [
    {
      kind: "hero",
      label: t("website.heroLabel", "Hero background"),
      hint: t("website.heroHint", "A wide photo shown behind your name — landscapes and studio shots work best (at least 1600 px wide)."),
      url: settings.heroImageUrl,
      inputRef: heroInputRef,
      previewClass: "h-32 w-full rounded-xl object-cover",
    },
    {
      kind: "photo",
      label: t("website.photoLabel", "Profile photo"),
      hint: t("website.photoHint", "A friendly portrait — shown next to your name."),
      url: settings.photoUrl,
      inputRef: photoInputRef,
      previewClass: "h-24 w-24 rounded-full object-cover",
    },
  ];

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.imagesTitle", "Images")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("website.imagesIntro", "Your landing page's hero background and your profile photo. JPEG, PNG or WebP, up to 8 MB.")}
      </p>

      {slots.map(({ kind, label, hint, url, inputRef, previewClass }) => (
        <div key={kind} className="mt-4">
          <p className="text-sm font-medium text-grey-20 dark:text-white">{label}</p>
          <p className="mt-0.5 text-xs text-grey-40 dark:text-grey-80">{hint}</p>
          {url && <img src={url} alt={label} className={`mt-2 ${previewClass}`} />}
          <div className="mt-2 flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(",")}
              className="hidden"
              onChange={(event) => {
                handleFile(kind, event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              disabled={status === "loading"}
              onClick={() => inputRef.current?.click()}
            >
              <ImagePlus size={16} />
              {url ? t("website.replaceImage", "Replace") : t("website.uploadImage", "Upload")}
            </button>
            {url && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={status === "loading"}
                onClick={() => dispatch(removeBookingPageImage(kind))}
              >
                <Trash2 size={16} />
                {t("website.removeImage", "Remove")}
              </button>
            )}
          </div>
          {kind === "hero" && <HeroPresetPicker />}
        </div>
      ))}

      {error && <p className="field-error mt-2">{error}</p>}
    </div>
  );
};

export default WebsiteImagesCard;
